/** @jsxImportSource @emotion/react */
import { FC, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Buttons } from '@zip/business-components';
import { useMerchantApplicationContext } from 'contexts/MerchantApplication.context';
import {
  CheckCircleIcon,
  ZipStripe as ZipStripeLockup,
} from 'public/illustrations';
import * as styles from './PartnerIntegrationModule.styles';
import { logEvent } from 'utils';
import { PageAction } from 'enums';

const STRIPE_INTEGRATION_URL =
  'https://dashboard.stripe.com/login?locale=en-AU';

type StripeStep = 'step1' | 'step2';

// ─── Check circle icon (Step 2 partner integration benefits) ─────────────────

function CheckCircle() {
  return (
    <div css={styles.checkCircleWrapper}>
      <div css={styles.checkCircleInner}>
        <img alt="" src={CheckCircleIcon} css={styles.checkCircleImage} />
      </div>
    </div>
  );
}

// ─── Step 1: Turn on Zip via Stripe ──────────────────────────────────────────

function StripeStep1View({
  onTurnOnViaStripe,
  onBecomeZipMerchant,
}: {
  onTurnOnViaStripe: () => void;
  onBecomeZipMerchant: () => void;
}) {
  return (
    <Box css={styles.container}>
      <Box css={styles.content}>
        <Box css={styles.card}>
          <Box css={styles.innerBox}>
            {/* Zip + Stripe logo lockup */}
            <Box css={styles.logoRow}>
              <img
                src={ZipStripeLockup}
                width={138}
                height={50}
                alt="Zip x Stripe"
              />
            </Box>

            {/* Text content */}
            <Box css={styles.textContent}>
              <p className="title4" css={styles.cardTitle}>
                Great news! You can turn on Zip Payments in minutes
              </p>
              <p className="body2" css={styles.cardBody}>
                Since you're already using Stripe as your payment provider, the
                fastest way to start accepting Zip is to switch us on directly
                in your Stripe dashboard — it's that easy!
              </p>
            </Box>

            {/* Primary CTA */}
            <Box css={styles.buttonWrapper}>
              <Buttons.Primary
                css={styles.primaryButton}
                variant="contained"
                onClick={onTurnOnViaStripe}
              >
                Turn on Zip via Stripe
              </Buttons.Primary>
            </Box>

            {/* Secondary link */}
            <p className="body2" css={styles.secondaryText}>
              Or you can unlock more advanced features by{' '}
              <a
                href="#"
                className="body1"
                css={styles.secondaryLink}
                onClick={(e) => {
                  e.preventDefault();
                  onBecomeZipMerchant();
                }}
              >
                becoming a Zip integrated Merchant
              </a>
              .
            </p>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Step 2: Become a Zip Merchant ───────────────────────────────────────────

const PARTNER_INTEGRATION_BENEFITS = [
  'Integrated Zip checkout',
  'Competitive pricing and not setup costs',
  'Exclusive marketing promotions',
  'Extended interest free periods',
];

function StripeStep2View() {
  const { nextSignUpStep, merchantApplicationId } =
    useMerchantApplicationContext();

  const handleFinaliseApplicationClicked = () => {
    logEvent(
      PageAction.merchantOnboardingUAStripeIntegrationFinaliseApplication,
      { merchantApplicationId }
    );
    nextSignUpStep();
  };

  return (
    <Box css={styles.container}>
      <Box css={styles.content}>
        <Box css={styles.card}>
          <Box css={styles.innerBox}>
            <p className="title4" css={styles.cardTitle}>
              Become a Zip Merchant to unlock more ways to grow
            </p>

            <Box css={styles.uspList}>
              {PARTNER_INTEGRATION_BENEFITS.map((benefit) => (
                <Box key={benefit} css={styles.uspItem}>
                  <CheckCircle />
                  <p className="body2" css={styles.uspText}>
                    {benefit}
                  </p>
                </Box>
              ))}
            </Box>

            <Box css={styles.buttonWrapper}>
              <Buttons.Primary
                css={styles.primaryButton}
                variant="contained"
                onClick={handleFinaliseApplicationClicked}
              >
                Finalise your application
              </Buttons.Primary>
            </Box>

            <p className="body2" css={styles.secondaryText}>
              Or turn on Zip Payments via your{' '}
              <a
                href={STRIPE_INTEGRATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="body1"
                css={styles.secondaryLink}
                onClick={() => {
                  logEvent(PageAction.merchantOnboardingUAStripeIntegration, {
                    merchantApplicationId,
                    location: 'step2link',
                  });
                }}
              >
                Stripe Dashboard
              </a>
              .
            </p>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main module ──────────────────────────────────────────────────────────────

export const PartnerIntegrationModule: FC = () => {
  const [step, setStep] = useState<StripeStep>('step1');
  const { merchantApplicationId } = useMerchantApplicationContext();

  const handleTurnOnViaStripe = () => {
    logEvent(PageAction.merchantOnboardingUAStripeIntegration, {
      merchantApplicationId,
      location: 'step1button',
    });
    window.open(STRIPE_INTEGRATION_URL, '_blank', 'noopener,noreferrer');
  };

  const handleBecomeZipMerchant = () => {
    logEvent(
      PageAction.merchantOnboardingUAStripeIntegrationZipIntegratedMerchant,
      { merchantApplicationId }
    );
    setStep('step2');
  };

  if (step === 'step2') {
    return <StripeStep2View />;
  }

  return (
    <StripeStep1View
      onTurnOnViaStripe={handleTurnOnViaStripe}
      onBecomeZipMerchant={handleBecomeZipMerchant}
    />
  );
};

export default PartnerIntegrationModule;
